/* ==========================================================================
   Shared Size Guide controller
   - One shell (#sg-drawer, rendered once via size-guide-shell.liquid)
   - Per-product data lives in `<script class="size-guide-data" data-product-id>`
   - Any element with class `.js-size-guide` + `data-product-id` opens it.
   Works on desktop PDP, mobile PDP and product cards (collections, recently
   viewed, cart, etc.). Content is looked up on demand so AJAX-injected cards
   work too.
   ========================================================================== */
(function () {
  'use strict';

  var LABELS = {
    en: {
      selectSize: 'Select a size',
      sizePrefix: 'Size ',
      unitCm: ' CM',
      unitIn: ' IN',
      brand: 'Nawa',
      desc: function (title) {
        return 'Use this size guide as a reference for <strong>' + title +
          '</strong> to find the fit that feels most comfortable on you.';
      },
      measure: { chest: 'Chest', waist: 'Waist', hip: 'Hips', hem: 'Hem', inseam: 'Inseam', outseam: 'Outseam' }
    },
    ar: {
      selectSize: 'اختر المقاس',
      sizePrefix: 'المقاس ',
      unitCm: ' سم',
      unitIn: ' بوصة',
      brand: 'Nawa',
      desc: function (title) {
        return 'استخدم دليل المقاسات هذا كمرجع لـ <strong>' + title +
          '</strong> للعثور على المقاس الأنسب لك.';
      },
      measure: { chest: 'الصدر', waist: 'الخصر', hip: 'الأرداف', hem: 'الحاشية', inseam: 'درزة البنطال الداخلية', outseam: 'الدرزة الخارجية' }
    }
  };

  var MEAS_KEYS = ['chest', 'waist', 'hip', 'hem', 'inseam', 'outseam'];

  var DEFAULT_HOW_TO_MEASURE = {
    dresses: {
      en: [
        'Chest measurement — Measure at the highest point',
        'Waist measurement — Measure at the narrowest point',
        'Hips measurement — Measure at the widest point'
      ],
      ar: [
        'قياس الصدر — القياس عند أعلى نقطة',
        'قياس الخصر — القياس عند أضيق نقطة',
        'قياس الأرداف — القياس عند أوسع نقطة'
      ]
    },
    tops_outerwear: {
      en: [
        'Chest measurement — Measure at the highest point',
        'Waist measurement — Measure at the narrowest point',
        'Hem measurement — Measure at the lowest point'
      ],
      ar: [
        'قياس الصدر — القياس عند أعلى نقطة',
        'قياس الخصر — القياس عند أضيق نقطة',
        'قياس الحاشية — القياس عند أسفل حافة'
      ]
    },
    trousers_skirts: {
      en: [
        'Waist measurement — Measure at the narrowest point',
        'Hips measurement — Measure at the widest point'
      ],
      ar: [
        'قياس الخصر — القياس عند أضيق نقطة',
        'قياس الأرداف — القياس عند أوسع نقطة'
      ]
    }
  };

  var state = {
    data: null,
    sizeIndex: -1,
    unit: 'cm'
  };

  function isArabic() {
    return (document.documentElement.lang || '').substring(0, 2) === 'ar';
  }

  function L() {
    return isArabic() ? LABELS.ar : LABELS.en;
  }

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function cleanSize(size) {
    return String(size == null ? '' : size).split('_')[0].trim();
  }

  // --- Shell element cache -------------------------------------------------
  var shell = null;
  function el() {
    if (shell) return shell;
    var drawer = document.getElementById('sg-drawer');
    if (!drawer) return null;
    shell = {
      drawer: drawer,
      overlay: document.querySelector('[data-sg-overlay]'),
      desc: drawer.querySelector('[data-sg-desc]'),
      selectWrapper: drawer.querySelector('[data-sg-select-wrapper]'),
      selectTrigger: drawer.querySelector('[data-sg-select-trigger]'),
      selectOptions: drawer.querySelector('[data-sg-select-options]'),
      selectedLabel: drawer.querySelector('[data-sg-selected-label]'),
      dynamicData: drawer.querySelector('[data-sg-dynamic-data]'),
      convSize: drawer.querySelector('[data-sg-conv-size]'),
      convEu: drawer.querySelector('[data-sg-conv-eu]'),
      convFr: drawer.querySelector('[data-sg-conv-fr]'),
      convUk: drawer.querySelector('[data-sg-conv-uk]'),
      convUs: drawer.querySelector('[data-sg-conv-us]'),
      convIt: drawer.querySelector('[data-sg-conv-it]'),
      measHeaders: drawer.querySelector('[data-sg-meas-headers]'),
      measValues: drawer.querySelector('[data-sg-meas-values]'),
      htmSection: drawer.querySelector('[data-sg-htm-section]'),
      htmList: drawer.querySelector('[data-sg-htm-list]'),
      unitBtns: drawer.querySelectorAll('.unit-btn'),
      imageNode: drawer.querySelector('.measure-size img')
    };
    return shell;
  }

  // --- Data lookup (on demand so AJAX cards work) --------------------------
  function findData(productId) {
    if (!productId) return null;
    var selector = '.size-guide-data[data-product-id="' +
      (window.CSS && CSS.escape ? CSS.escape(productId) : productId) + '"]';
    var node = document.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      console.error('[Size Guide] Failed to parse data for product', productId, e);
      return null;
    }
  }

  // --- Rendering -----------------------------------------------------------
  function renderHowToMeasure(s, data) {
    var guideType = data.guide_type || 'dresses';
    var def = DEFAULT_HOW_TO_MEASURE[guideType] || DEFAULT_HOW_TO_MEASURE.dresses;
    var items = isArabic() ? def.ar : def.en;

    // Use custom metafield how_to_measure ONLY if it contains detailed descriptions
    if (data.how_to_measure && data.how_to_measure.length) {
      var hasDetailedText = data.how_to_measure.some(function(item) {
        var str = String(item);
        return str.indexOf(' — ') !== -1 || str.indexOf(' - ') !== -1 || str.indexOf(' : ') !== -1;
      });
      if (hasDetailedText) {
        items = data.how_to_measure;
      }
    }

    if (!items || !items.length) {
      s.htmSection.style.display = 'none';
      return;
    }

    s.htmList.innerHTML = '';
    items.forEach(function (item) {
      var parts = String(item).split(' — ');
      if (parts.length === 1) parts = String(item).split(' - ');
      if (parts.length === 1) parts = String(item).split(' : ');
      var title = parts[0] ? parts[0].trim() : '';
      var desc = parts[1] ? parts[1].trim() : '';
      var li = document.createElement('li');
      li.className = 'how-to-measure-item';
      li.innerHTML =
        '<div class="how-to-measure-num">' + escapeHtml(title) + '</div>' +
        (desc ? '<div class="how-to-measure-text">' + escapeHtml(desc) + '</div>' : '');
      s.htmList.appendChild(li);
    });
    s.htmSection.style.display = 'block';
  }

  function renderOptions(s, data) {
    s.selectOptions.innerHTML = '';
    data.sizes.forEach(function (row, index) {
      var opt = document.createElement('div');
      opt.className = 'custom-select-option';
      opt.textContent = cleanSize(row.size);
      opt.setAttribute('data-index', index);
      s.selectOptions.appendChild(opt);
    });
  }

  function renderMeasurements(s) {
    if (state.sizeIndex === -1) return;
    var row = state.data.sizes[state.sizeIndex];
    var meas = row[state.unit] || {};
    var t = L().measure;
    var unitLabel = state.unit === 'cm' ? L().unitCm : L().unitIn;

    // Active keys ordered by MEAS_KEYS (chest before waist)
    var active = MEAS_KEYS.filter(function (k) {
      return meas[k] !== null && meas[k] !== undefined && meas[k] !== '';
    });

    s.measHeaders.innerHTML = '<th></th><th></th>';
    active.forEach(function (k) {
      var th = document.createElement('th');
      th.textContent = t[k] || k;
      s.measHeaders.appendChild(th);
    });

    s.measValues.innerHTML = '<td>' + L().brand + '</td><td>' + escapeHtml(cleanSize(row.size)) + '</td>';
    active.forEach(function (k) {
      var td = document.createElement('td');
      td.textContent = meas[k] + unitLabel;
      s.measValues.appendChild(td);
    });
  }

  function selectSize(s, index) {
    state.sizeIndex = index;
    var row = state.data.sizes[index];
    var clean = cleanSize(row.size);

    s.selectedLabel.textContent = L().sizePrefix + clean;

    Array.prototype.forEach.call(s.selectOptions.querySelectorAll('.custom-select-option'), function (opt) {
      opt.classList.toggle('selected', parseInt(opt.getAttribute('data-index'), 10) === index);
    });

    s.dynamicData.style.display = 'block';

    if (s.convSize) s.convSize.textContent = clean;
    var c = row.conversion || {};
    s.convEu.textContent = c.eu || '-';
    s.convFr.textContent = c.fr || '-';
    s.convUk.textContent = c.uk || '-';
    s.convUs.textContent = c.us || '-';
    s.convIt.textContent = c.it || '-';

    renderMeasurements(s);
  }

  // --- Open / close --------------------------------------------------------
  function open(productId) {
    var s = el();
    if (!s) return false;
    var data = findData(productId);
    if (!data || !data.sizes || !data.sizes.length) return false;

    state.data = data;
    state.sizeIndex = -1;
    state.unit = 'cm';

    // Update dynamic image if present
    if (s.imageNode && data.guide_image) {
      s.imageNode.src = data.guide_image;
    }

    // Reset UI
    s.desc.innerHTML = L().desc(escapeHtml(data.title || ''));
    s.selectedLabel.textContent = L().selectSize;
    s.dynamicData.style.display = 'none';
    s.selectWrapper.classList.remove('open');
    Array.prototype.forEach.call(s.unitBtns, function (b) {
      b.classList.toggle('active', b.getAttribute('data-unit') === 'cm');
    });

    renderOptions(s, data);
    renderHowToMeasure(s, data);

    document.body.classList.add('sg-open');
    s.drawer.setAttribute('aria-hidden', 'false');
    return true;
  }

  function close() {
    var s = el();
    if (!s) return;
    document.body.classList.remove('sg-open');
    s.selectWrapper.classList.remove('open');
    s.drawer.setAttribute('aria-hidden', 'true');
  }

  // --- Event wiring --------------------------------------------------------
  function init() {
    var s = el();
    if (!s) return;

    // Open triggers (delegated, capture phase to pre-empt any legacy modal handlers)
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest ? e.target.closest('.js-size-guide') : null;
      if (!trigger) return;
      var pid = trigger.getAttribute('data-product-id');
      if (!pid) return;
      if (open(pid)) {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      }
    }, true);

    // Close: overlay + close button
    s.overlay.addEventListener('click', close);
    s.drawer.querySelector('[data-sg-close]').addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && document.body.classList.contains('sg-open')) close();
    });

    // Dropdown toggle
    s.selectTrigger.addEventListener('click', function (e) {
      e.stopPropagation();
      s.selectWrapper.classList.toggle('open');
    });
    s.selectOptions.addEventListener('click', function (e) {
      var opt = e.target.closest('.custom-select-option');
      if (!opt) return;
      e.stopPropagation();
      selectSize(s, parseInt(opt.getAttribute('data-index'), 10));
      s.selectWrapper.classList.remove('open');
    });
    // Close dropdown when clicking elsewhere inside the drawer
    s.drawer.addEventListener('click', function (e) {
      if (!e.target.closest('[data-sg-select-wrapper]')) {
        s.selectWrapper.classList.remove('open');
      }
    });

    // Unit toggle
    Array.prototype.forEach.call(s.unitBtns, function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        Array.prototype.forEach.call(s.unitBtns, function (b) { b.classList.remove('active'); });
        btn.classList.add('active');
        state.unit = btn.getAttribute('data-unit');
        renderMeasurements(s);
      });
    });
  }

  // Public API (optional external use)
  window.SizeGuide = { open: open, close: close };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
